import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, 
  Mail, 
  MapPin, 
  Download,
  GraduationCap,
  Briefcase,
  Award,
  Calendar,
  BookMarked,
  ExternalLink,
  Menu,
  X,
  BookOpen
} from 'lucide-react';
import './Landing.css';

// Custom Drop-in Replacement for the missing Linkedin Icon
const LinkedinIcon = ({ size = 24, className = "" }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
    <rect width="4" height="12" x="2" y="9"/>
    <circle cx="4" cy="4" r="2"/>
  </svg>
);

const WilliamLeRoyLanding = () => {
  const [papers, setPapers] = useState([]);
  const [papersLoading, setPapersLoading] = useState(true);
  const [papersError, setPapersError] = useState(false);

  const [activities, setActivities] = useState({
    conferences: [],
    presentations_academic_year: [],
    teaching: [],
    media_coverage: []
  });
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [activitiesError, setActivitiesError] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetch('./papers.json')
      .then(response => response.ok ? response.json() : fetch('/papers.json').then(res => res.json()))
      .then(data => { setPapers(data); setPapersLoading(false); })
      .catch(error => { console.error('Error fetching papers:', error); setPapersError(true); setPapersLoading(false); });
  }, []);

  useEffect(() => {
    fetch('./activities.json')
      .then(response => response.ok ? response.json() : fetch('/activities.json').then(res => res.json()))
      .then(data => { setActivities(data); setActivitiesLoading(false); })
      .catch(error => { console.error('Error fetching activities:', error); setActivitiesError(true); setActivitiesLoading(false); });
  }, []);

  const timelineEvents = [
    ...(activities.conferences || []).map(conf => ({
      type: 'conference',
      title: conf.name,
      meta: conf.date,
      url: conf.url,
      icon: Calendar,
      badgeColor: '#e06666'
    })),
    ...(activities.presentations_academic_year || []).map(pres => ({
      type: 'presentation',
      title: pres,
      meta: '2025–2026 Academic Year',
      url: null,
      icon: Briefcase,
      badgeColor: '#4a90e2'
    })),
    ...(activities.teaching || []).map(t => ({
      type: 'teaching',
      title: t,
      meta: 'Recent Course',
      url: null,
      icon: BookMarked,
      badgeColor: '#50e3c2'
    }))
  ];

  const filteredEvents = selectedCategory === 'all' 
    ? timelineEvents 
    : timelineEvents.filter(e => e.type === selectedCategory);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2, delayChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 100 } }
  };

  const handleScroll = (e, targetId) => {
    e.preventDefault();
    setMobileMenuOpen(false);

    setTimeout(() => {
      const target = document.getElementById(targetId);
      if (target) {
        const headerOffset = 90; 
        const elementPosition = target.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.scrollY - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth"
        });
      }
    }, 10);
  };

  return (
    <div className="landing-page">
      <style>{`
        .mobile-menu-btn { display: none !important; }
        .desktop-nav { display: flex !important; }
        @media (max-width: 768px) {
          .mobile-menu-btn { display: flex !important; }
          .desktop-nav { display: none !important; }
        }
      `}</style>

      <nav className="nav-bar" style={{
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        backdropFilter: 'blur(12px)',
        background: 'rgba(15, 15, 15, 0.85)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        transition: 'all 0.3s ease'
      }}>
        <div className="nav-container" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          width: '100%', 
          maxWidth: '1200px', 
          margin: '0 auto', 
          padding: '0 20px', 
          height: '70px',
          boxSizing: 'border-box'
        }}>
          <div className="logo" style={{ fontSize: '18px', fontWeight: '700', letterSpacing: '-0.5px', color: '#fff' }}>
            William <span className="highlight" style={{ color: '#e06666' }}>LeRoy</span>
          </div>

          <ul className="nav-links desktop-nav" style={{ marginLeft: 'auto', gap: '24px', listStyle: 'none', padding: 0, alignItems: 'center' }}>
            <li><a href="#home" onClick={(e) => handleScroll(e, 'home')} style={{ color: '#b0b0b0', textDecoration: 'none', fontSize: '14px', fontWeight: '500', transition: 'color 0.2s', cursor: 'pointer' }}>Home</a></li>
            <li><a href="#current-research-notes" onClick={(e) => handleScroll(e, 'current-research-notes')} style={{ color: '#b0b0b0', textDecoration: 'none', fontSize: '14px', fontWeight: '500', transition: 'color 0.2s', cursor: 'pointer' }}>Research</a></li>
            <li><a href="#activities" onClick={(e) => handleScroll(e, 'activities')} style={{ color: '#b0b0b0', textDecoration: 'none', fontSize: '14px', fontWeight: '500', transition: 'color 0.2s', cursor: 'pointer' }}>Activities</a></li>
          </ul>

          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="mobile-menu-btn"
            style={{
              marginLeft: 'auto',
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '8px',
              padding: '8px',
              color: '#fff',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1001
            }}
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              style={{
                background: 'rgba(15, 15, 15, 0.95)',
                borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                overflow: 'hidden',
                padding: '20px 24px',
                boxSizing: 'border-box'
              }}
            >
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <li>
                  <a href="#home" onClick={(e) => handleScroll(e, 'home')} style={{ color: '#fff', textDecoration: 'none', fontSize: '16px', fontWeight: '600', display: 'block', cursor: 'pointer' }}>Home</a>
                </li>
                <li>
                  <a href="#current-research-notes" onClick={(e) => handleScroll(e, 'current-research-notes')} style={{ color: '#fff', textDecoration: 'none', fontSize: '16px', fontWeight: '600', display: 'block', cursor: 'pointer' }}>Research</a>
                </li>
                <li>
                  <a href="#activities" onClick={(e) => handleScroll(e, 'activities')} style={{ color: '#fff', textDecoration: 'none', fontSize: '16px', fontWeight: '600', display: 'block', cursor: 'pointer' }}>Activities</a>
                </li>
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      <main className="main-content">

        <motion.section initial="hidden" animate="visible" variants={containerVariants} className="hero-section" id="home" style={{ scrollMarginTop: '90px' }}>
          <motion.div variants={itemVariants} className="sidebar">
            <div className="profile-photo-container group" style={{ overflow: 'hidden', borderRadius: '12px' }}>
              <img 
                src="./profile.jpg" 
                alt="William LeRoy" 
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} 
              />
            </div>

            <div className="contact-card">
              <div className="contact-item align-start">
                <MapPin size={18} className="icon-highlight pin-adjust" />
                <span>Faculty of Law <br />Charles University <br />Prague</span>
              </div>
              <div className="contact-item">
                <Mail size={18} className="icon-highlight" />
                <a href="mailto:william.leroy@prf.cuni.cz">william.leroy@prf.cuni.cz</a>
              </div>
              <div className="contact-item">
                <LinkedinIcon size={18} className="icon-highlight" />
                <a href="https://www.linkedin.com/in/william-leroy-phd-437745141/" target="_blank" rel="noopener noreferrer">LinkedIn</a>
              </div>
              <div className="contact-item">
                <BookOpen size={18} className="icon-highlight" />
                <a href="https://scholar.google.com/citations?user=5jKRVXQAAAAJ&hl=en&oi=ao" target="_blank" rel="noopener noreferrer">Google Scholar</a>
              </div>
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="main-intro">
            <h1 className="hero-title" style={{ margin: 0 }}>Advancing Research in <br /><span className="text-gradient">Political Economy</span></h1>

            {/* Inlined Bio/About Text next to the CV Download Button */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '24px', flexWrap: 'wrap', marginTop: '24px' }}>
              <a 
                href="https://drive.google.com/uc?id=1T9dHssMtgUJ35uDt-leZKENdqfVtH14R&export=download" 
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: 'rgba(224, 102, 102, 0.15)',
                  color: '#e06666',
                  border: '1px solid rgba(224, 102, 102, 0.3)',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '600',
                  textDecoration: 'none',
                  transition: 'all 0.2s ease',
                  flexShrink: 0,
                  marginTop: '2px'
                }}
                className="hover-highlight"
              >
                <Download size={15} /> Download CV
              </a>

              <div className="bio-text" style={{ fontSize: '12px', color: '#999', lineHeight: '1.6', display: 'flex', flexDirection: 'column', gap: '8px', flex: '1 1 300px' }}>
                <p style={{ margin: 0 }}><strong style={{ color: '#ccc' }}>Bio:</strong> I am an empirical social scientist studying the effects of global migration and armed conflict. My recent work examines the New York City migrant housing crisis, small-boat crossings across the English Channel, the Israel–Hamas War, and the Russo–Ukrainian War.</p>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginTop: '32px' }}>
              <div style={{ background: 'rgba(15, 15, 15, 0.85)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '8px', padding: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#4a90e2', fontWeight: '600', fontSize: '13px', marginBottom: '4px' }}>
                  <Briefcase size={16} /> Appointment
                </div>
                <div style={{ color: '#fff', fontSize: '14px', fontWeight: '500' }}>Assistant Professor</div>
                <div style={{ color: '#888', fontSize: '12px' }}>Charles University</div>
              </div>

              <div style={{ background: 'rgba(15, 15, 15, 0.85)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '8px', padding: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#50e3c2', fontWeight: '600', fontSize: '13px', marginBottom: '4px' }}>
                  <Award size={16} /> Research Focus
                </div>
                <div style={{ color: '#fff', fontSize: '14px', fontWeight: '500', lineHeight: '1.3' }}>Empirical Social Science</div>
                <div style={{ color: '#888', fontSize: '12px' }}>Migration & Armed Conflict</div>
              </div>

              <div style={{ background: 'rgba(15, 15, 15, 0.85)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '8px', padding: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#e06666', fontWeight: '600', fontSize: '13px', marginBottom: '4px' }}>
                  <MapPin size={16} /> Location
                </div>
                <div style={{ color: '#fff', fontSize: '14px', fontWeight: '500' }}>Prague</div>
                <div style={{ color: '#888', fontSize: '12px' }}>Czech Republic</div>
              </div>
            </div>

            <div style={{ marginTop: '32px' }}>
              <h3 style={{ fontSize: '11px', color: '#888', marginBottom: '20px', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: 'bold' }}>
                Education
              </h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '24px' }}>
                <div style={{ position: 'relative', paddingLeft: '16px', borderLeft: '2px solid rgba(255,255,255,0.1)' }}>
                  <div style={{ position: 'absolute', left: '-5px', top: '5px', width: '8px', height: '8px', borderRadius: '50%', background: '#e06666', boxShadow: '0 0 0 2px #0f0f0f' }}></div>
                  <div style={{ color: '#262626', fontSize: '14px', fontWeight: '600' }}>Ph.D. in Economics</div>
                  <div style={{ color: '#888', fontSize: '13px', marginTop: '4px' }}>University of Maryland (2025)</div>
                </div>
                
                <div style={{ position: 'relative', paddingLeft: '16px', borderLeft: '2px solid rgba(255,255,255,0.1)' }}>
                  <div style={{ position: 'absolute', left: '-5px', top: '5px', width: '8px', height: '8px', borderRadius: '50%', background: '#4a90e2', boxShadow: '0 0 0 2px #0f0f0f' }}></div>
                  <div style={{ color: '#262626', fontSize: '14px', fontWeight: '600' }}>M.A. in Economics</div>
                  <div style={{ color: '#888', fontSize: '13px', marginTop: '4px' }}>University of Maryland (2021)</div>
                </div>
                
                <div style={{ position: 'relative', paddingLeft: '16px', borderLeft: '2px solid rgba(255,255,255,0.1)' }}>
                  <div style={{ position: 'absolute', left: '-5px', top: '5px', width: '8px', height: '8px', borderRadius: '50%', background: '#50e3c2', boxShadow: '0 0 0 2px #0f0f0f' }}></div>
                  <div style={{ color: '#262626', fontSize: '14px', fontWeight: '600' }}>B.A. in Economics</div>
                  <div style={{ color: '#888', fontSize: '13px', marginTop: '4px' }}>University of Notre Dame (2019)</div>
                </div>
                
                <div style={{ position: 'relative', paddingLeft: '16px', borderLeft: '2px solid rgba(255,255,255,0.1)' }}>
                  <div style={{ position: 'absolute', left: '-5px', top: '5px', width: '8px', height: '8px', borderRadius: '50%', background: '#888', boxShadow: '0 0 0 2px #0f0f0f' }}></div>
                  <div style={{ color: '#262626', fontSize: '14px', fontWeight: '600' }}>Undergraduate Studies</div>
                  <div style={{ color: '#888', fontSize: '13px', marginTop: '4px' }}>Colgate University (Transferred)</div>
                </div>
              </div>
            </div>

          </motion.div>
        </motion.section>

        <hr className="divider" />

        <section className="cv-section" id="current-research-notes" style={{ scrollMarginTop: '90px' }}>
          <div className="cv-header-flex">
            <div>
              <h2 className="section-title">Research</h2>
              <p className="section-subtitle">Selected working papers and publications on global migration, public economics, and political economy.</p>
            </div>
          </div>

          <div style={{ maxWidth: '900px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {papersLoading && <div style={{ color: '#666', padding: '20px 0' }}>Loading research papers...</div>}

            {papersError && (
              <div style={{ color: 'red', padding: '20px 0' }}>
                Unable to load research papers at this time.
              </div>
            )}

            {!papersLoading && !papersError && papers.map((paper, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                whileHover={{ scale: 1.01 }}
                style={{ 
                  position: 'relative',
                  overflow: 'hidden',
                  background: '#121212',
                  borderLeft: '6px solid #e06666',
                  borderRadius: '8px',
                  padding: '30px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.15)'
                }}
              >
                {paper.videoUrl ? (
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1, pointerEvents: 'none', overflow: 'hidden' }}>
                    <video autoPlay loop muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.75 }}>
                      <source src={paper.videoUrl} type="video/webm" />
                    </video>
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'linear-gradient(to right, rgba(18,18,18,0.7), rgba(18,18,18,0.9))' }} />
                  </div>
                ) : (
                  <motion.div 
                    animate={{ 
                      backgroundPosition: ['0% 0%', '100% 100%'],
                      scale: [1, 1.05, 1]
                    }}
                    transition={{ 
                      duration: 20, 
                      repeat: Infinity, 
                      repeatType: 'reverse' 
                    }}
                    style={{
                      position: 'absolute',
                      top: 0, left: 0, right: 0, bottom: 0,
                      backgroundImage: paper.bgEffect === 'gaza-map' 
                        ? 'radial-gradient(circle at 80% 20%, rgba(224, 102, 102, 0.15) 0%, transparent 60%), linear-gradient(to right, rgba(20,20,20,0.9), rgba(10,10,10,0.95))'
                        : 'radial-gradient(circle at 20% 80%, rgba(70, 130, 180, 0.15) 0%, transparent 60%), linear-gradient(to right, rgba(20,20,20,0.9), rgba(10,10,10,0.95))',
                      zIndex: 1,
                      pointerEvents: 'none'
                    }}
                  />
                )}

                <div style={{ position: 'relative', zIndex: 2 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <span style={{ fontSize: '12px', color: '#e06666', textTransform: 'uppercase', fontWeight: 'bold', letterSpacing: '1px' }}>
                      {paper.date}
                    </span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {paper.tags && paper.tags.map((tag, tIndex) => (
                        <span key={tIndex} style={{ fontSize: '11px', background: 'rgba(255,255,255,0.08)', color: '#ccc', padding: '3px 8px', borderRadius: '4px' }}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  <h3 style={{ margin: '0 0 12px 0', fontSize: '22px', color: '#ffffff', fontWeight: '600' }}>
                    {paper.title}
                  </h3>

                  <p style={{ margin: '0 0 20px 0', color: '#b0b0b0', lineHeight: '1.6', fontSize: '15px' }}>
                    {paper.abstract}
                  </p>

                  <motion.a 
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                    href={paper.pdfUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    style={{ 
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: '#e06666', 
                      color: 'white', 
                      padding: '10px 20px', 
                      textDecoration: 'none', 
                      borderRadius: '6px', 
                      fontWeight: 'bold', 
                      fontSize: '14px',
                      boxShadow: '0 4px 12px rgba(224, 102, 102, 0.3)'
                    }}
                  >
                    <FileText size={16} /> View Paper / PDF
                  </motion.a>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        <hr className="divider" />

        <section className="cv-section" id="activities" style={{ scrollMarginTop: '90px' }}>
          <div className="cv-header-flex" style={{ marginBottom: '24px' }}>
            <div>
              <h2 className="section-title">Academic Calendar & Activities</h2>
              <p className="section-subtitle">A chronological view of conferences, talks, and teaching engagements.</p>
            </div>

            <div style={{ display: 'flex', gap: '8px', background: '#3b3b3b', padding: '4px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.08)' }}>
              {['all', 'conference', 'presentation', 'teaching'].map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  style={{
                    background: selectedCategory === cat ? '#e06666' : 'transparent',
                    color: selectedCategory === cat ? '#fff' : '#888',
                    border: 'none',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    textTransform: 'capitalize',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div style={{ maxWidth: '900px', margin: '0 auto', minHeight: '600px' }}>
            {activitiesLoading && <div style={{ color: '#666', padding: '20px 0' }}>Loading calendar stream...</div>}

            {activitiesError && (
              <div style={{ color: 'red', padding: '20px 0' }}>
                Unable to load activity schedule at this time.
              </div>
            )}

            {!activitiesLoading && !activitiesError && (
              <div style={{ position: 'relative', borderLeft: '2px solid rgba(224, 102, 102, 0.25)', marginLeft: '20px', paddingLeft: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <AnimatePresence>
                  {filteredEvents.map((event, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: -15 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 15 }}
                      transition={{ duration: 0.3, delay: idx * 0.05 }}
                      whileHover={{ scale: 1.01, borderColor: 'rgba(224, 102, 102, 0.3)' }}
                      style={{
                        position: 'relative',
                        background: 'rgba(15, 15, 15, 0.85)',
                        border: '1px solid rgba(255,255,255,0.06)',
                        borderRadius: '12px',
                        padding: '22px',
                        boxShadow: '0 8px 30px rgba(0,0,0,0.25)',
                        backdropFilter: 'blur(8px)'
                      }}
                    >
                      <div style={{
                        position: 'absolute',
                        left: '-33px',
                        top: '24px',
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        background: event.badgeColor,
                        border: '3px solid #0b0b0b',
                        boxShadow: `0 0 12px ${event.badgeColor}88`
                      }} />

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <span style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 'bold', color: event.badgeColor, background: `${event.badgeColor}15`, padding: '3px 8px', borderRadius: '4px' }}>
                          {event.type}
                        </span>
                        <span style={{ fontSize: '13px', color: '#888', display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <Calendar size={13} /> {event.meta}
                        </span>
                      </div>

                      <h3 style={{ fontSize: '17px', color: '#fff', fontWeight: '600', margin: '0 0 4px 0', lineHeight: '1.4' }}>
                        {event.url ? (
                          <a href={event.url} target="_blank" rel="noopener noreferrer" style={{ color: '#fff', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' }} className="hover-highlight">
                            <span style={{ textDecoration: 'underline' }}>{event.title}</span>
                            <ExternalLink size={14} color="#888" />
                          </a>
                        ) : (
                          event.title
                        )}
                      </h3>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </div>
        </section>

      </main>
    </div>
  );
};

export default WilliamLeRoyLanding;